import { PrintDocument } from '@/components/ui/PrintDocument';
import { env } from '@/config/env';
import { SUBSCRIPTION_PAYMENT_METHOD_LABEL } from '@/constants/institute';
import { formatAmountInWords, formatCurrency, formatDate } from '@/lib/format';
import type { SubscriptionBilledTo, SubscriptionPayment, SubscriptionReceipts } from '@/types/institute';

/** One line of a receipt's detail block. */
function Detail({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="py-0.5 pr-3 align-top font-semibold">{label}</dt>
      <dd className="py-0.5 pr-6">{value}</dd>
    </>
  );
}

/**
 * A licence receipt, laid out for paper — what an institute paid us.
 *
 * Plain on purpose, like the school fee receipt: black on white, no cards and
 * no colour, because it is printed and filed. Headed with our product name from
 * config rather than anything written here, and made out to the institute's
 * registered name, which is what its accounts office files it under.
 *
 * It states the renewal date as recorded with the payment and nothing about
 * the account today: a copy printed next year must say what this receipt said
 * the day it was issued.
 */
function SubscriptionReceipt({
  payment,
  institute,
}: {
  payment: SubscriptionPayment;
  institute: SubscriptionBilledTo;
}) {
  return (
    <article className="break-after-page bg-white p-6 text-black last:break-after-auto">
      <header className="border-b-2 border-black pb-3 text-center">
        <h1 className="text-xl font-bold">{env.appName}</h1>
        <p className="mt-2 text-sm font-semibold uppercase tracking-widest">
          Subscription payment receipt
        </p>
      </header>

      <div className="mt-4 flex flex-wrap justify-between gap-x-6 gap-y-1 text-sm">
        <p>
          <span className="font-semibold">Receipt no.</span>{' '}
          <span className="font-mono">{payment.receiptNumber}</span>
        </p>
        <p>
          <span className="font-semibold">Date of payment</span> {formatDate(payment.paidOn)}
        </p>
      </div>

      <dl className="mt-4 grid grid-cols-[auto_1fr] text-sm sm:grid-cols-[auto_1fr_auto_1fr]">
        <Detail label="Received from" value={institute.legalName} />
        <Detail label="Institute code" value={institute.code.toUpperCase()} />
        <Detail label="Location" value={`${institute.city}, ${institute.state}`} />
        {institute.gstin && <Detail label="GSTIN" value={institute.gstin} />}
      </dl>

      <table className="mt-5 w-full border-collapse text-sm">
        <thead>
          <tr className="border-y border-black text-left">
            <th className="py-2">Particulars</th>
            <th className="py-2 text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-b border-black/20">
            <td className="py-1.5">
              Software subscription for {payment.periodYear}
              <span className="block text-xs">Renews on {formatDate(payment.renewsOn)}</span>
            </td>
            <td className="py-1.5 text-right font-mono">{formatCurrency(payment.amount)}</td>
          </tr>
          <tr className="border-b-2 border-black font-bold">
            <td className="py-2">Total received</td>
            <td className="py-2 text-right font-mono">{formatCurrency(payment.amount)}</td>
          </tr>
        </tbody>
      </table>

      <p className="mt-3 text-sm">
        <span className="font-semibold">In words</span> {formatAmountInWords(payment.amount)}
      </p>
      <p className="mt-1 text-sm">
        <span className="font-semibold">Paid by</span>{' '}
        {SUBSCRIPTION_PAYMENT_METHOD_LABEL[payment.method]}
        {payment.referenceNo && ` · ${payment.referenceNo}`}
      </p>
      {payment.note && (
        <p className="mt-1 text-sm">
          <span className="font-semibold">Note</span> {payment.note}
        </p>
      )}

      <div className="mt-12 flex items-end justify-between text-sm">
        <span>{payment.recordedBy ? `Recorded by ${payment.recordedBy}` : ''}</span>
        <span className="border-t border-black pt-1">Authorised signatory</span>
      </div>

      <p className="mt-6 text-center text-xs">
        Computer generated receipt. Please keep it for your records.
      </p>
    </article>
  );
}

export interface SubscriptionReceiptsPrintProps {
  /** What to print, or null when nothing is being printed. */
  target: SubscriptionReceipts | null;
  onFinished: () => void;
}

/**
 * Sends one receipt, or every receipt, to the browser's print dialog — where
 * "Save as PDF" is the download. One receipt per sheet either way, so a bundle
 * splits back into the same pages a single download would have given.
 */
export function SubscriptionReceiptsPrint({ target, onFinished }: SubscriptionReceiptsPrintProps) {
  const fileName = !target
    ? 'Receipt'
    : target.payments.length === 1
      ? `Receipt-${target.payments[0].receiptNumber}`
      : `Receipts-${target.institute.code.toUpperCase()}`;

  return (
    <PrintDocument isOpen={target !== null} onFinished={onFinished} fileName={fileName}>
      {target?.payments.map((payment) => (
        <SubscriptionReceipt key={payment.uuid} payment={payment} institute={target.institute} />
      ))}
    </PrintDocument>
  );
}
